/**
 * 測定履歴の送り書きと読み出し。
 *
 * ブラウザから直接 /api/typing/sessions を叩く。ここは Cloudflare Worker が
 * api-go へ中継する経路で、Cookie もそのまま渡る (/api/auth/v1/me と同じ)。
 *
 * **リソースルートを挟まない。** 順位表は挟んでいて、そのせいで画面側の
 * fetcher が付ける .data ごと api-go に飛び、404 でページごと落ちていた。
 * 同じ形にしない。
 */

import { clearHistory } from "./history";
import type { TypingHistoryDeleteResult } from "./history-delete";
import {
  EMPTY_HISTORY,
  type TypingHistory,
  type TypingHistoryPoint,
  type TypingHistoryRow,
} from "./history.remote";

const ENDPOINT = "/api/typing/sessions";

export type TypingHistorySubmission = {
  clientRunId: string;
  mode: string;
  durationMs: number;
  totalKeystrokes: number;
  missCount: number;
  measuredMs: number;
  stability: number;
  /** 同じミリ秒に重なった打鍵の数 */
  simultaneousCount: number;
  /** 観測した最小の正の間隔。粗い時計では重なりを判定しない */
  clockMs: number;
  caught: number;
  escaped: number;
  blazing: number;
  earnedTokens: number;
  pasted: boolean;
  blurred: boolean;
  weakKeys: unknown[];
};

/**
 * 1 回ぶんを残す。
 *
 * 失敗しても投げない。**打ち終わった直後の画面を落とさない。**
 * 記録が残らないのは残念だが、結果そのものは目の前に出ている
 */
export async function saveTypingSession(
  submission: TypingHistorySubmission,
): Promise<boolean> {
  try {
    const res = await fetch(ENDPOINT, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "same-origin",
      body: JSON.stringify(submission),
    });
    return res.ok;
  } catch {
    return false;
  }
}

/**
 * サーバーと端末の両方から、自分の記録を消す。
 *
 * **端末は必ず消す。** サーバーが落ちても、通信が届かなくても、
 * 目の前の端末からは消えてほしい。逆にサーバーだけ消えて端末が
 * 残ると、次に開いたときに「消したのに出てくる」に見える。
 *
 * **誰の記録を消すかは本文に書かない。** api-go はセッションから
 * user_id を取る (rest_typing_history.go の deleteAll)。こちらから
 * 名乗れる欄を送ると、その欄が他人の記録を消す口になる。
 */
export async function deleteTypingHistory(): Promise<TypingHistoryDeleteResult> {
  const local = clearHistory();

  let res: Response;
  try {
    res = await fetch(ENDPOINT, {
      method: "DELETE",
      credentials: "same-origin",
    });
  } catch {
    return { server: null, local, failed: true };
  }

  // ログインしていなければサーバーには最初から何も無い。失敗ではない
  if (res.status === 401) return { server: null, local, failed: false };
  if (!res.ok) return { server: null, local, failed: true };

  let body: unknown;
  try {
    body = await res.json();
  } catch {
    // 消えてはいるが、件数が読めない。数を偽らずに null で返す
    return { server: null, local, failed: false };
  }
  const parsed = (body ?? {}) as { sessions?: unknown; rankingEntries?: unknown };
  return {
    server: {
      sessions: typeof parsed.sessions === "number" ? parsed.sessions : 0,
      rankingEntries:
        typeof parsed.rankingEntries === "number" ? parsed.rankingEntries : 0,
    },
    local,
    failed: false,
  };
}

/** 自分の履歴を取る。コースごとに分けて取る */
export async function fetchTypingHistory(
  durationMs: number,
): Promise<TypingHistory> {
  let res: Response;
  try {
    res = await fetch(`${ENDPOINT}?durationMs=${durationMs}`, {
      credentials: "same-origin",
    });
  } catch {
    return { ...EMPTY_HISTORY, failed: true };
  }
  // ログインしていないだけなら失敗ではない
  if (res.status === 401) return { ...EMPTY_HISTORY, signedOut: true };
  if (!res.ok) return { ...EMPTY_HISTORY, failed: true };

  let body: unknown;
  try {
    body = await res.json();
  } catch {
    // リソースルートと違って HTML が返ることは無いが、
    // 中継が壊れたときに 0 件と同じ顔にしない
    return { ...EMPTY_HISTORY, failed: true };
  }
  if (!body || typeof body !== "object") {
    return { ...EMPTY_HISTORY, failed: true };
  }

  const parsed = body as { points?: unknown; recent?: unknown };
  return {
    points: Array.isArray(parsed.points) ? parsed.points.filter(isPoint) : [],
    recent: Array.isArray(parsed.recent) ? parsed.recent.filter(isRow) : [],
    failed: false,
    signedOut: false,
  };
}

function isPoint(value: unknown): value is TypingHistoryPoint {
  if (!value || typeof value !== "object") return false;
  const p = value as Partial<TypingHistoryPoint>;
  return typeof p.date === "string" && typeof p.effectiveKpm === "number";
}

function isRow(value: unknown): value is TypingHistoryRow {
  if (!value || typeof value !== "object") return false;
  const r = value as Partial<TypingHistoryRow>;
  return typeof r.id === "string" && typeof r.recordedAt === "string";
}
